/**
 * VideoEditorNode (Canvas V2)
 *
 * Stitch video clips, add audio, and render edited video.
 * Features:
 * - 840px fixed width, 3 invisible handles
 * - Edge-based video/audio clip resolution (V1 pattern)
 * - Preview area: empty state, running state, completed state with hover autoplay
 * - DRAFT badge when clips differ from last render
 * - AgentNodeToolbar: Open Editor (opens TimelineEditorModal), Expand, Download, Duplicate, Delete
 * - Download dialog (portal)
 * - Video lightbox (portal, z-9999)
 * - Stale running status reset on mount
 */

import { memo, useState, useRef, useEffect, useCallback, useMemo } from 'react'
import { createPortal } from 'react-dom'
import { Handle, Position, type NodeProps, useReactFlow, useEdges, useNodes } from 'reactflow'
import { MessageSquarePlus, BookmarkPlus } from 'lucide-react'
import { buildProjectAssetUrl, executeNode, addEdgesToGraph, removeEdgesFromGraph } from '../../../../api/client'
import { useAuthenticatedMediaUrl } from '../../../../hooks/useAuthenticatedMediaUrl'
import {
  AgentNodeToolbar,
  useNodeActions,
  type ToolbarAction,
} from '../shared'
import { ReferenceSelector, type SelectedReference } from '../shared/ReferenceSelector'
import { TimelineEditorModal } from '../../panels/timeline/TimelineEditorModal'
import { VideoEditorBottomPanel } from './VideoEditorBottomPanel'
import { useProjectId } from '../../../../contexts/ProjectIdContext'
import type { GeneratedVideoWithHistory, VideoEditorNodeData } from '../../../../generated/nodeDataTypes'

/* ── Types ── */

export type { VideoEditorNodeData }

/** Node fixed width per spec */
const NODE_WIDTH = 840

/** Status indicator config */
const STATUS_CONFIG: Record<string, { color: string; glow: boolean; text: string }> = {
  idle: { color: '#404055', glow: false, text: 'Ready' },
  running: { color: '#eab308', glow: true, text: 'Editing...' },
  completed: { color: '#22c55e', glow: true, text: 'Done' },
  error: { color: '#ef4444', glow: true, text: 'Error' },
}

const DEFAULT_STATUS = { color: '#404055', glow: false, text: 'Unknown' }

/* ── Helpers ── */

function formatDuration(seconds: number): string {
  const mins = Math.floor(seconds / 60)
  const secs = Math.floor(seconds % 60)
  return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`
}

/* ── Component ── */

export const VideoEditorNode = memo(function VideoEditorNode(props: NodeProps<VideoEditorNodeData>) {
  const { id, data, selected, dragging } = props
  const {
    out_video_output: video_output,
    out_rendered_clip_paths,
    status = 'idle',
    progressMessage,
    error,
  } = data
  const projectId = useProjectId()

  const { setNodes } = useReactFlow()
  const edges = useEdges()
  const allNodes = useNodes()
  const { handleDelete, handleDuplicate, handleDownload: downloadFile, handleAddToChat, handleSaveToLibrary } = useNodeActions({ nodeId: id })

  const [isVideoModalOpen, setIsVideoModalOpen] = useState(false)
  const [isHovering, setIsHovering] = useState(false)
  const [videoDuration, setVideoDuration] = useState<number | null>(null)
  const [activeClipIndex, setActiveClipIndex] = useState(0)
  const [showDownloadDialog, setShowDownloadDialog] = useState(false)
  const [isRenderingForDownload, setIsRenderingForDownload] = useState(false)
  const [renderProgress, setRenderProgress] = useState(0)
  const [renderMessage, setRenderMessage] = useState('')
  const [lightboxClipIndex, setLightboxClipIndex] = useState(0)
  const [isTimelineEditorOpen, setIsTimelineEditorOpen] = useState(false)
  const [bottomPanelOpen, setBottomPanelOpen] = useState(false)
  const [isRefSelectorOpen, setIsRefSelectorOpen] = useState(false)
  const handleOpenReferenceSelector = useCallback(() => setIsRefSelectorOpen(true), [])
  const videoRef = useRef<HTMLVideoElement>(null)
  const lightboxVideoRef = useRef<HTMLVideoElement>(null)

  /* ── Resolve connected video clips from edges ── */
  const resolvedVideoClips = useMemo(() => {
    const clipEdges = edges.filter(
      (e) =>
        e.target === id &&
        (e.targetHandle === 'in_video_paths' || e.targetHandle === 'in_video_path'),
    )
    return clipEdges
      .map((edge) => {
        const sourceNode = allNodes.find((n) => n.id === edge.source)
        const sourceData = sourceNode?.data as Record<string, unknown> | undefined

        // Priority: remixed video > out_video_path > out_generated_video history[picked] > video_output > local_path
        const genVideo = sourceData?.out_generated_video as GeneratedVideoWithHistory | undefined
        const pickedIdx = genVideo?.picked_index ?? 0
        const genVideoPath = genVideo?.history?.[pickedIdx]?.result?.local_path
        const videoPath = (
          sourceData?.out_remixed_video ||
          sourceData?.out_video_path ||
          genVideoPath ||
          sourceData?.out_video ||
          sourceData?.video_output ||
          sourceData?.local_path
        ) as string | undefined

        if (!videoPath) return null

        return {
          id: edge.source,
          path: videoPath,
          name: videoPath.split('/').pop() || 'Untitled',
        }
      })
      .filter(Boolean) as Array<{ id: string; path: string; name: string }>
  }, [edges, id, allNodes])

  /* ── Resolve connected audio clips from edges ── */
  const resolvedAudioClips = useMemo(() => {
    const audioEdges = edges.filter(
      (e) =>
        e.target === id &&
        (e.targetHandle === 'in_audio_paths' || e.targetHandle === 'in_audio_path'),
    )
    return audioEdges
      .map((edge) => {
        const sourceNode = allNodes.find((n) => n.id === edge.source)
        const sourceData = sourceNode?.data as Record<string, unknown> | undefined

        const audioPath = (
          sourceData?.out_audio_file ||
          sourceData?.audio_output ||
          sourceData?.audio_file ||
          sourceData?.local_path
        ) as string | undefined

        if (!audioPath) return null

        return {
          id: edge.source,
          path: audioPath,
          name: audioPath.split('/').pop() || 'Untitled',
          role: (sourceData?.role as 'original' | 'replacement' | 'sfx' | undefined),
        }
      })
      .filter(Boolean) as Array<{ id: string; path: string; name: string; role?: 'original' | 'replacement' | 'sfx' }>
  }, [edges, id, allNodes])

  /* ── Current references for ReferenceSelector ── */
  const currentReferences = useMemo(() => {
    const refs: SelectedReference[] = []
    for (const clip of resolvedVideoClips) {
      const node = allNodes.find((n) => n.id === clip.id)
      refs.push({
        nodeId: clip.id,
        nodeType: node?.type ?? 'video',
        label: clip.name,
        assetPath: clip.path,
        mediaType: 'video',
      })
    }
    for (const clip of resolvedAudioClips) {
      const node = allNodes.find((n) => n.id === clip.id)
      refs.push({
        nodeId: clip.id,
        nodeType: node?.type ?? 'audio',
        label: clip.name,
        assetPath: clip.path,
        mediaType: 'audio',
      })
    }
    return refs
  }, [resolvedVideoClips, resolvedAudioClips, allNodes])

  /* ── Save handler for ReferenceSelector (edge-based) ── */
  const handleRefSave = useCallback(
    (selected: SelectedReference[]) => {
      setIsRefSelectorOpen(false)
      if (!projectId) return

      // Collect existing video/audio edges to remove
      const oldEdges = edges.filter(
        (e) =>
          e.target === id &&
          (e.targetHandle === 'in_video_paths' ||
            e.targetHandle === 'in_video_path' ||
            e.targetHandle === 'in_audio_paths' ||
            e.targetHandle === 'in_audio_path'),
      )

      // Remove old edges via REST (backend handles graph + YDoc sync)
      if (oldEdges.length > 0) {
        removeEdgesFromGraph(
          projectId,
          oldEdges.map((e) => ({
            from: e.source,
            to: e.target,
            fromPort: e.sourceHandle || undefined,
            toPort: e.targetHandle || undefined,
          })),
        ).catch((err) => console.error('[VideoEditor:refSave] removeEdges failed:', err))
      }

      // Add new edges via REST — fromPort omitted, backend resolves via resolve_edge_ports()
      const newEdges = selected.map((ref) => ({
        from: ref.nodeId,
        to: id,
        toPort: ref.mediaType === 'video' ? 'in_video_paths' : 'in_audio_paths',
        // fromPort intentionally omitted — backend resolves via resolve_edge_ports()
      }))
      if (newEdges.length > 0) {
        addEdgesToGraph(projectId, newEdges, {
          intent: 'Add references via VideoEditor',
        }).catch((err) => console.error('[VideoEditor:refSave] addEdges failed:', err))
      }
    },
    [edges, id, projectId],
  )

  /* ── Draft detection ── */
  const isDraft = useMemo(() => {
    if (resolvedVideoClips.length === 0) return false
    if (!video_output) return true
    if (!out_rendered_clip_paths || out_rendered_clip_paths.length === 0) return false
    const currentPaths = resolvedVideoClips.map((c) => c.path)
    if (currentPaths.length !== out_rendered_clip_paths.length) return true
    return currentPaths.some((p, i) => p !== out_rendered_clip_paths[i])
  }, [resolvedVideoClips, video_output, out_rendered_clip_paths])

  /* ── Build video URLs ── */
  const activeClip = resolvedVideoClips[activeClipIndex] ?? null
  const baseClipUrl = useMemo(() => {
    if (!activeClip || !projectId) return null
    return buildProjectAssetUrl(projectId, activeClip.path)
  }, [activeClip, projectId])
  const clipVideoUrl = useAuthenticatedMediaUrl(baseClipUrl, data.cacheKey)

  const baseRenderedUrl = useMemo(() => {
    if (!video_output || !projectId) return null
    return buildProjectAssetUrl(projectId, video_output)
  }, [video_output, projectId])
  const renderedVideoUrl = useAuthenticatedMediaUrl(baseRenderedUrl, data.cacheKey)

  const previewVideoUrl = useMemo(() => {
    if (!isDraft && renderedVideoUrl) return renderedVideoUrl
    return clipVideoUrl
  }, [isDraft, renderedVideoUrl, clipVideoUrl])

  const isShowingRenderedVideo = !isDraft && !!renderedVideoUrl

  // Lightbox clip URL
  const lightboxClip = resolvedVideoClips[lightboxClipIndex] ?? null
  const baseLightboxClipUrl = useMemo(() => {
    if (!lightboxClip || !projectId) return null
    return buildProjectAssetUrl(projectId, lightboxClip.path)
  }, [lightboxClip, projectId])
  const lightboxClipUrl = useAuthenticatedMediaUrl(baseLightboxClipUrl, data.cacheKey)

  /* ── Reset stale running status on mount ── */
  useEffect(() => {
    if (status === 'running') {
      setNodes((nds) =>
        nds.map((n) =>
          n.id === id
            ? {
                ...n,
                data: {
                  ...n.data,
                  status: 'idle',
                  progress: undefined,
                  progressMessage: undefined,
                  error: 'Edit interrupted. Click Render to retry.',
                },
              }
            : n,
        ),
      )
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  /* ── Hover play/pause ── */
  useEffect(() => {
    if (isHovering && previewVideoUrl && videoRef.current) {
      videoRef.current.play().catch(() => {})
    } else if (!isHovering && videoRef.current) {
      videoRef.current.pause()
      videoRef.current.currentTime = 0
      setActiveClipIndex(0)
    }
  }, [isHovering, previewVideoUrl])

  /* ── Lightbox autoplay ── */
  useEffect(() => {
    if (isVideoModalOpen && lightboxVideoRef.current) {
      lightboxVideoRef.current.play().catch(() => {})
    }
  }, [isVideoModalOpen, lightboxClipUrl])

  const currentStatus = STATUS_CONFIG[status] || DEFAULT_STATUS
  const hasClips = resolvedVideoClips.length > 0

  const handleLoadedMetadata = () => {
    if (videoRef.current && isFinite(videoRef.current.duration)) {
      setVideoDuration(videoRef.current.duration)
    }
  }

  const handleVideoEnded = () => {
    if (resolvedVideoClips.length > 0) {
      setActiveClipIndex((prev) => (prev + 1) % resolvedVideoClips.length)
    }
  }

  const handleLightboxVideoEnded = () => {
    if (resolvedVideoClips.length > 0) {
      setLightboxClipIndex((prev) => (prev + 1) % resolvedVideoClips.length)
    }
  }

  /* ── Download ── */
  const doDownload = useCallback(
    (url: string) => {
      const filename = video_output?.split('/').pop() || 'edited_video.mp4'
      downloadFile(url, filename)
    },
    [video_output, downloadFile],
  )

  const handleDownload = useCallback(() => {
    if (renderedVideoUrl && !isDraft) {
      doDownload(renderedVideoUrl)
      return
    }
    setShowDownloadDialog(true)
  }, [renderedVideoUrl, isDraft, doDownload])

  /* ── Render now (for download dialog) ── */
  const handleRenderNow = useCallback(async () => {
    if (!projectId) return
    setIsRenderingForDownload(true)
    setRenderProgress(0)
    setRenderMessage('Preparing...')

    try {
      await executeNode(projectId, id)
      // On success the Yjs sync will update data.video_output
      setIsRenderingForDownload(false)
      setShowDownloadDialog(false)
    } catch (err) {
      console.error('Render failed:', err)
      setIsRenderingForDownload(false)
      setRenderMessage(`Error: ${err}`)
    }
  }, [projectId, id])

  /* ── Toolbar actions ── */
  const toolbarActions: ToolbarAction[] = useMemo(
    () => [
      {
        key: 'open-editor',
        label: 'Open Editor',
        icon: (
          <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M9 17V7m0 10a2 2 0 01-2 2H5a2 2 0 01-2-2V7a2 2 0 012-2h2a2 2 0 012 2m0 10a2 2 0 002 2h2a2 2 0 002-2M9 7a2 2 0 012-2h2a2 2 0 012 2m0 10V7m0 10a2 2 0 002 2h2a2 2 0 002-2V7a2 2 0 00-2-2h-2a2 2 0 00-2 2"
            />
          </svg>
        ),
        onClick: () => {
          setIsTimelineEditorOpen(true)
        },
        variant: 'primary' as const,
        disabled: !hasClips,
      },
      {
        key: 'expand',
        label: 'Expand',
        icon: (
          <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M4 8V4m0 0h4M20 8V4m0 0h-4M4 16v4m0 0h4M20 16v4m0 0h-4"
            />
          </svg>
        ),
        onClick: () => {
          setLightboxClipIndex(0)
          setIsVideoModalOpen(true)
        },
        hidden: !video_output && !hasClips,
        group: true,
      },
      {
        key: 'download',
        label: 'Download',
        icon: (
          <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"
            />
          </svg>
        ),
        onClick: handleDownload,
        hidden: !video_output && !hasClips,
      },
      {
        key: 'addToChat',
        label: 'Add to Chat',
        icon: <MessageSquarePlus size={14} />,
        onClick: handleAddToChat,
      },
      {
        key: 'save-to-library',
        label: 'Save to Library',
        icon: <BookmarkPlus className="w-4 h-4" />,
        onClick: handleSaveToLibrary,
        group: true,
      },
      {
        key: 'duplicate',
        label: 'Duplicate',
        icon: (
          <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"
            />
          </svg>
        ),
        onClick: handleDuplicate,
        group: true,
      },
      {
        key: 'delete',
        label: 'Delete',
        icon: (
          <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
            />
          </svg>
        ),
        onClick: handleDelete,
        variant: 'danger' as const,
      },
    ],
    [hasClips, video_output, handleDownload, handleDuplicate, handleDelete, handleAddToChat, handleSaveToLibrary],
  )

  /* ── Render ── */
  return (
    <div className="relative">
      <AgentNodeToolbar actions={toolbarActions} isVisible={!!selected && !dragging} />

      <div
        className={`rounded-lg shadow transition-all bg-card border group relative ${
          selected ? 'ring-2 ring-white/70 border-white/40' : 'border-border'
        }`}
        style={{ width: NODE_WIDTH }}
      >
        {/* Connection Handles (3 invisible handles) */}
        <Handle
          type="target"
          position={Position.Top}
          id="in_video_paths"
          className="!bg-transparent !border-0 !w-4 !h-1"
        />
        <Handle
          type="target"
          position={Position.Left}
          id="in_audio_paths"
          className="!bg-transparent !border-0 !w-1 !h-4"
          style={{ top: '50%' }}
        />
        <Handle
          type="source"
          position={Position.Bottom}
          id="out_video_output"
          className="!bg-transparent !border-0 !w-4 !h-1"
        />

        {/* Header */}
        <div className="px-3 py-2 bg-card-hover border-b border-border flex items-center justify-between rounded-t-lg">
          <div className="flex items-center gap-2">
            <span className="text-xs font-semibold text-foreground">Video Editor</span>
            {hasClips && (
              <span className="text-xxs text-foreground/30 bg-surface-1 px-1.5 py-0.5 rounded">
                {resolvedVideoClips.length} clip{resolvedVideoClips.length !== 1 ? 's' : ''}
              </span>
            )}
          </div>
          <div className="flex items-center gap-2">
            <div
              className={`w-2.5 h-2.5 rounded-full ${status === 'running' ? 'animate-pulse' : ''}`}
              style={{
                background: currentStatus.color,
                boxShadow: currentStatus.glow ? `0 0 8px ${currentStatus.color}` : 'none',
              }}
              title={currentStatus.text}
            />
          </div>
        </div>

        {/* Preview Area */}
        <div
          className="relative w-full bg-canvas overflow-hidden cursor-pointer group"
          style={{ height: NODE_WIDTH * (9 / 16) }}
          onMouseEnter={() => setIsHovering(true)}
          onMouseLeave={() => setIsHovering(false)}
        >
          {status === 'running' ? (
            <div className="absolute inset-0 flex flex-col items-center justify-center px-4">
              <div className="w-8 h-8 border-2 border-yellow-500 border-t-transparent rounded-full animate-spin" />
              <span className="text-xxs text-foreground/50 mt-2 text-center leading-relaxed">
                {progressMessage || 'Processing...'}
              </span>
            </div>
          ) : hasClips || renderedVideoUrl ? (
            <>
              <video
                ref={videoRef}
                src={(previewVideoUrl || renderedVideoUrl) ?? undefined}
                className="w-full h-full object-cover"
                muted
                playsInline
                preload="metadata"
                loop={isShowingRenderedVideo}
                onLoadedMetadata={handleLoadedMetadata}
                onEnded={isShowingRenderedVideo ? undefined : handleVideoEnded}
              />
              {/* Play button overlay */}
              {!isHovering && (
                <div className="absolute inset-0 flex items-center justify-center bg-black/20 group-hover:bg-transparent transition-colors">
                  <div className="w-12 h-12 rounded-full bg-black/50 backdrop-blur-sm flex items-center justify-center border border-border text-foreground/90 group-hover:scale-110 transition-transform">
                    <svg className="w-6 h-6 ml-1" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M8 5v14l11-7z" />
                    </svg>
                  </div>
                </div>
              )}
              {/* Duration badge */}
              {videoDuration !== null && (
                <div className="absolute bottom-2 left-2 bg-black/70 backdrop-blur-sm text-foreground text-xxs font-mono px-1.5 py-0.5 rounded">
                  {formatDuration(videoDuration)}
                </div>
              )}
              {/* DRAFT badge */}
              {isDraft && (
                <div className="absolute bottom-2 right-2 bg-yellow-500/80 text-black text-xxs font-bold px-1.5 py-0.5 rounded">
                  DRAFT
                </div>
              )}
              {/* Clip counter during hover */}
              {isHovering && !isShowingRenderedVideo && resolvedVideoClips.length > 1 && (
                <div className="absolute top-2 left-2 bg-black/70 backdrop-blur-sm text-foreground text-xxs font-mono px-1.5 py-0.5 rounded">
                  {activeClipIndex + 1}/{resolvedVideoClips.length}
                </div>
              )}
              {/* Overlay action buttons */}
              <div className="absolute top-2 right-2 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity z-10">
                <button
                  onClick={(e) => {
                    e.stopPropagation()
                    setLightboxClipIndex(0)
                    setIsVideoModalOpen(true)
                  }}
                  className="w-7 h-7 rounded bg-black/60 hover:bg-black/80 text-foreground flex items-center justify-center backdrop-blur-sm transition-colors"
                  title="Expand"
                >
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8V4m0 0h4M20 8V4m0 0h-4M4 16v4m0 0h4M20 16v4m0 0h-4" />
                  </svg>
                </button>
                <button
                  onClick={(e) => {
                    e.stopPropagation()
                    handleDownload()
                  }}
                  className="w-7 h-7 rounded bg-black/60 hover:bg-black/80 text-foreground flex items-center justify-center backdrop-blur-sm transition-colors"
                  title="Download"
                >
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                  </svg>
                </button>
              </div>
            </>
          ) : (
            <div className="absolute inset-0 flex flex-col items-center justify-center text-foreground/20">
              <svg className="w-10 h-10 mb-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={1}
                  d="M7 4v16M17 4v16M3 8h4m10 0h4M3 12h18M3 16h4m10 0h4M4 20h16a1 1 0 001-1V5a1 1 0 00-1-1H4a1 1 0 00-1 1v14a1 1 0 001 1z"
                />
              </svg>
              <span className="text-xxs">Connect video nodes</span>
              {error && (
                <span
                  className="text-xxs text-red-400 mt-1 px-2 text-center line-clamp-2 cursor-help"
                  title={error}
                >
                  {error}
                </span>
              )}
            </div>
          )}
        </div>

        {/* Error footer */}
        {error && (hasClips || renderedVideoUrl) && (
          <div className="px-3 py-2 bg-red-500/10 border-t border-red-500/30">
            <span className="text-xxs text-red-400 line-clamp-2 cursor-help" title={error}>
              {error}
            </span>
          </div>
        )}

        {/* Quick Info Bar */}
        <div className="grid grid-cols-2 border-t border-border rounded-b-lg overflow-hidden">
          <button
            onClick={(e) => {
              e.stopPropagation()
              setBottomPanelOpen(!bottomPanelOpen)
            }}
            className="py-2 px-3 flex items-center gap-2 hover:bg-card-hover transition-colors text-left nodrag"
          >
            <span className="text-xs font-medium text-foreground">References</span>
            <span className="text-xxs text-foreground/40 bg-surface-1 px-1.5 py-0.5 rounded">
              {resolvedVideoClips.length + resolvedAudioClips.length}
            </span>
          </button>
          <button
            onClick={(e) => {
              e.stopPropagation()
              setIsTimelineEditorOpen(true)
            }}
            className="py-2 px-3 flex items-center gap-2 hover:bg-card-hover transition-colors text-left border-l border-border nodrag"
            disabled={!hasClips}
          >
            <span className={`text-xs font-medium ${hasClips ? 'text-foreground' : 'text-foreground/30'}`}>Editor</span>
          </button>
        </div>
      </div>

      {/* Bottom Panel */}
      <VideoEditorBottomPanel
        isVisible={!!selected && !dragging && bottomPanelOpen}
        videoClips={resolvedVideoClips}
        audioClips={resolvedAudioClips}
        onOpenReferenceSelector={handleOpenReferenceSelector}
        onOpenEditor={() => setIsTimelineEditorOpen(true)}
      />

      {/* Download Dialog (portal) */}
      {showDownloadDialog &&
        createPortal(
          <div
            className="fixed inset-0 z-[9999] bg-black/80 backdrop-blur-sm flex items-center justify-center p-8"
            onClick={() => !isRenderingForDownload && setShowDownloadDialog(false)}
          >
            <div
              className="bg-card border border-border rounded-md shadow-2xl p-6 max-w-md w-full"
              onClick={(e) => e.stopPropagation()}
            >
              <h3 className="text-sm font-semibold text-foreground mb-3">Download Video</h3>

              {isRenderingForDownload ? (
                <div className="space-y-3">
                  <div className="flex items-center gap-3">
                    <div className="w-5 h-5 border-2 border-teal-500 border-t-transparent rounded-full animate-spin" />
                    <span className="text-xs text-text-secondary">{renderMessage}</span>
                  </div>
                  <div className="h-2 bg-black/30 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-gradient-to-r from-teal-500 to-teal-400 transition-all duration-300"
                      style={{ width: `${renderProgress}%` }}
                    />
                  </div>
                  <span className="text-xxs text-text-muted">{renderProgress}%</span>
                </div>
              ) : (
                <>
                  <p className="text-xs text-foreground/50 mb-4">
                    {!video_output
                      ? 'No rendered video yet. Render your clips to download.'
                      : "Recent edits haven't been rendered. The download contains the previous version."}
                  </p>

                  <div className="flex gap-2 justify-end">
                    <button
                      onClick={() => setShowDownloadDialog(false)}
                      className="px-3 py-1.5 text-xs text-text-secondary hover:text-foreground/80 transition-colors"
                    >
                      Cancel
                    </button>
                    {video_output && renderedVideoUrl && (
                      <button
                        onClick={() => {
                          doDownload(renderedVideoUrl)
                          setShowDownloadDialog(false)
                        }}
                        className="px-3 py-1.5 text-xs bg-surface-2 hover:bg-border text-foreground rounded-lg transition-colors"
                      >
                        Download Previous
                      </button>
                    )}
                    <button
                      onClick={handleRenderNow}
                      className="px-3 py-1.5 text-xs bg-teal-600 hover:bg-teal-700 text-foreground rounded-lg transition-colors"
                    >
                      Render Now
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>,
          document.body,
        )}

      {/* Video Lightbox (portal) */}
      {isVideoModalOpen &&
        createPortal(
          <div
            className="fixed inset-0 z-[9999] bg-black/90 backdrop-blur-sm flex items-center justify-center p-8"
            onClick={() => setIsVideoModalOpen(false)}
          >
            <div
              className="relative w-full max-w-6xl max-h-full flex flex-col items-center justify-center"
              onClick={(e) => e.stopPropagation()}
            >
              <button
                onClick={() => setIsVideoModalOpen(false)}
                className="absolute -top-12 right-0 text-text-secondary hover:text-foreground transition-colors"
              >
                <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
              {isShowingRenderedVideo ? (
                <video
                  ref={lightboxVideoRef}
                  src={renderedVideoUrl ?? undefined}
                  controls
                  autoPlay
                  className="max-w-full max-h-[85vh] rounded-lg shadow-2xl bg-black"
                />
              ) : hasClips ? (
                <>
                  <video
                    ref={lightboxVideoRef}
                    src={lightboxClipUrl ?? undefined}
                    controls
                    autoPlay
                    className="max-w-full max-h-[85vh] rounded-lg shadow-2xl bg-black"
                    onEnded={handleLightboxVideoEnded}
                  />
                  {resolvedVideoClips.length > 1 && (
                    <div className="mt-3 flex items-center gap-2 text-foreground/50 text-xs">
                      <span>
                        Clip {lightboxClipIndex + 1} of {resolvedVideoClips.length}
                      </span>
                      <span className="text-foreground/30">|</span>
                      <span className="text-text-muted truncate max-w-[300px]">
                        {resolvedVideoClips[lightboxClipIndex]?.name}
                      </span>
                    </div>
                  )}
                </>
              ) : null}
            </div>
          </div>,
          document.body,
        )}

      {/* Timeline Editor Modal */}
      {isTimelineEditorOpen && (
        <TimelineEditorModal
          nodeId={id}
          videoClips={resolvedVideoClips}
          audioClips={resolvedAudioClips}
          onClose={() => setIsTimelineEditorOpen(false)}
        />
      )}

      {/* Reference Selector Modal */}
      <ReferenceSelector
        isOpen={isRefSelectorOpen}
        onClose={() => setIsRefSelectorOpen(false)}
        onSave={handleRefSave}
        excludeNodeId={id}
        canvasNodes={allNodes}
        currentReferences={currentReferences}
        maxReferences={20}
        visibleTabs={['video', 'audio']}
      />
    </div>
  )
})

VideoEditorNode.displayName = 'VideoEditorNode'
